import { createSlice, type PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

import { notificationsService, type UnreadNotifications } from "../services/notifications.service";
import type { FetchNotificationsParams } from "../services/notifications.service";
import type { NotificationItem } from "../types/notifications-api.types";

export type NotificationsConnectionStatus = "idle" | "connecting" | "connected" | "disconnected" | "error";

export interface NotificationsState {
  items: NotificationItem[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  connectionStatus: NotificationsConnectionStatus;
  /**
   * The server's answer to "what is unread", across every page: the ids it listed and how many there are. The
   * loaded page alone cannot say it (twenty read ones on top would hide an older pending one). Null until the
   * server answers; `selectUnreadNotificationsCount` puts it together with what happened here since.
   */
  unread: UnreadNotifications | null;
  /** Notifications read here or seen read since the session started. A read never goes back to unread. */
  readIds: string[];
  /** Unread notifications that arrived over the socket. */
  pushedUnreadIds: string[];
  /**
   * The first-page read in flight, and what was pushed while it was out: its answer was put together before
   * those arrived, so it must not drop them. A newer read supersedes an older one, whose answer is ignored.
   */
  listRequest: { requestId: string; pushed: NotificationItem[] } | null;
  /** The unread read in flight; an older answer that lands after a newer one is ignored. */
  unreadRequestId: string | null;
}

const initialState: NotificationsState = {
  items: [],
  total: 0,
  skip: 0,
  limit: 20,
  hasMore: false,
  isLoading: false,
  error: null,
  connectionStatus: "idle",
  unread: null,
  readIds: [],
  pushedUnreadIds: [],
  listRequest: null,
  unreadRequestId: null,
};

/** Records a read, and marks the notification read if it is loaded. */
function markRead(state: NotificationsState, id: string): void {
  if (!state.readIds.includes(id)) state.readIds.push(id);
  const item = state.items.find((n) => n.id === id);
  if (item && !item.read_at) item.read_at = new Date().toISOString();
}

/** A notification from an answer, with any read the answer did not know about yet. */
function withKnownRead(
  item: NotificationItem,
  readIds: ReadonlySet<string>,
  loaded: Map<string, NotificationItem>,
) {
  if (item.read_at || !readIds.has(item.id)) return item;
  return { ...item, read_at: loaded.get(item.id)?.read_at ?? new Date().toISOString() };
}

/**
 * How many notifications are pending: what the server counted, minus the ones it listed that were read here
 * since, plus the ones pushed since that it could not have listed. Exact whichever order the answer, the
 * pushes and the reads arrive in; before the server answers, the loaded page's own count.
 */
export function selectUnreadNotificationsCount(state: { notifications: NotificationsState }): number {
  const { unread, readIds, pushedUnreadIds, items } = state.notifications;
  if (!unread) return items.filter((n) => !n.read_at).length;
  const read = new Set(readIds);
  const listed = new Set(unread.ids);
  const readSince = unread.ids.filter((id) => read.has(id)).length;
  const pushedSince = pushedUnreadIds.filter((id) => !listed.has(id) && !read.has(id)).length;
  return Math.max(0, unread.total - readSince + pushedSince);
}

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchList",
  async (params: FetchNotificationsParams | undefined, { rejectWithValue }) => {
    try {
      return await notificationsService.fetchList(params);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error al cargar notificaciones";
      return rejectWithValue(message);
    }
  },
);

export const fetchUnreadNotifications = createAsyncThunk(
  "notifications/fetchUnread",
  async (endpoint: string | undefined, { rejectWithValue }) => {
    try {
      return await notificationsService.fetchUnread(endpoint);
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : "unread_failed");
    }
  },
);

export const markNotificationAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (params: { id: string; endpoint?: string } | string, { rejectWithValue }) => {
    try {
      if (typeof params === "string") {
        await notificationsService.markAsRead({ id: params });
        return params;
      }
      await notificationsService.markAsRead(params);
      return params.id;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error al marcar como leída";
      return rejectWithValue(message);
    }
  },
);

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<NotificationsConnectionStatus>) => {
      state.connectionStatus = action.payload;
    },
    pushNotification: (state, action: PayloadAction<NotificationItem>) => {
      const exists = state.items.some((n) => n.id === action.payload.id);
      if (!exists) {
        const item = withKnownRead(action.payload, new Set(state.readIds), new Map());
        state.items = [item, ...state.items];
        state.total = Math.max(state.total, state.items.length);
        state.listRequest?.pushed.push(item);
        if (!item.read_at && !state.pushedUnreadIds.includes(item.id)) state.pushedUnreadIds.push(item.id);
      }
    },
    setNotificationRead: (state, action: PayloadAction<string>) => {
      markRead(state, action.payload);
    },
    clearNotificationsError: (state) => {
      state.error = null;
    },
    resetNotificationsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        if ((action.meta.arg?.skip ?? 0) === 0) {
          state.listRequest = { requestId: action.meta.requestId, pushed: [] };
        }
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const isAppend = action.payload.skip > 0;
        const readIds = new Set(state.readIds);
        const loaded = new Map(state.items.map((n) => [n.id, n]));
        if (isAppend) {
          state.isLoading = false;
          const newItems = action.payload.data
            .filter((n) => !loaded.has(n.id))
            .map((n) => withKnownRead(n, readIds, loaded));
          state.items = [...state.items, ...newItems];
        } else {
          // An older first page landing after a newer one would put back what that one already replaced.
          if (state.listRequest?.requestId !== action.meta.requestId) return;
          state.isLoading = false;
          const answered = new Set(action.payload.data.map((n) => n.id));
          const pushedMeanwhile = state.listRequest.pushed.filter((n) => !answered.has(n.id));
          state.items = [
            ...pushedMeanwhile.map((n) => withKnownRead(n, readIds, loaded)),
            ...action.payload.data.map((n) => withKnownRead(n, readIds, loaded)),
          ];
          state.listRequest = null;
        }
        state.total = Math.max(action.payload.total, state.items.length);
        state.skip = action.payload.skip;
        state.limit = action.payload.limit;
        state.hasMore = action.payload.hasMore;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        if ((action.meta.arg?.skip ?? 0) === 0) {
          if (state.listRequest?.requestId !== action.meta.requestId) return;
          state.listRequest = null;
        }
        state.isLoading = false;
        state.error = (action.payload as string) ?? "Error al cargar notificaciones";
      })
      .addCase(fetchUnreadNotifications.pending, (state, action) => {
        state.unreadRequestId = action.meta.requestId;
      })
      .addCase(fetchUnreadNotifications.fulfilled, (state, action) => {
        if (state.unreadRequestId !== action.meta.requestId) return;
        state.unread = action.payload;
        state.unreadRequestId = null;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        markRead(state, action.payload);
      });
  },
});

export const {
  setConnectionStatus,
  pushNotification,
  setNotificationRead,
  clearNotificationsError,
  resetNotificationsState,
} = notificationsSlice.actions;
